import { Component, ComponentChildren } from 'preact';
import { ProductTemplate } from '../../common/data/designs.js';
import { DesignPicture } from './image.js';
import { Icon } from './icons.js';
import { maybe, throttle } from '../../common/util.js';
import { PublicProduct, WithFeatures } from '../../common/data/products.js';
import { classNames } from '../lib/util.js';

export interface ProductSliderProps {
	className?: string;
	product: PublicProduct & WithFeatures;
	productTemplates: ProductTemplate[];
	title?: string | ComponentChildren;
	size?: 'small' | 'large';
	handleClick?: (pt: ProductTemplate, e: Event) => void;
	link: (pt: ProductTemplate) => string;
}

interface ProductSliderState {
	short?: boolean;
	leftArrow?: boolean;
	rightArrow?: boolean;
	showUpTo: number;
}

export class ProductSlider extends Component<ProductSliderProps, ProductSliderState> {

	content: HTMLElement | null = null;
	mouseDown = false;
	dragging = false;
	initialX = 0;
	initialScroll = 0;
	
	constructor(props: ProductSliderProps) {
		super(props);
		this.state = {
			showUpTo: 6,
		};
	}
	
	render({ product, className, productTemplates, title, size }: ProductSliderProps, { short, leftArrow, rightArrow, showUpTo }: ProductSliderState) {
		const orient = maybe(product.features?.imageSizes?.[0], ([w, h]) => w > h) ? 'landscape' : 'portrait';
		const previewWidth = orient == 'landscape' ? 192 : 128;
		return <div className={classNames('product-slider', `--${size || 'small'}`, `--${orient}`, className)}>
			
			{/*{title && <h2>{title}</h2>}*/}
			
			<div
				class={`product-slider__content product-slider__content--${short ? 'short' : 'long'}`}
				ref={it => this.content = it}
				onMouseDown={e => {
					e.preventDefault();
					this.mouseDown = true;
					this.initialX = e.pageX;
					this.initialScroll = this.content!.scrollLeft;
				}}
				onTouchStart={e => {
					this.mouseDown = true;
					this.initialX = e.touches[0]!.pageX;
					this.initialScroll = this.content!.scrollLeft;
				}}
				onClick={e => {
					if (this.dragging) e.preventDefault();
				}}
				onScroll={this.update}
			>
				{productTemplates.map((p, i) => <a
					class="product-slider__product"
					onClick={(e) => this.props.handleClick?.(p, e)}
					href={this.props.link(p)}
				>
					{i <= showUpTo && <DesignPicture
						designId={null}
						product={p.product}
						productTemplate={p.id}
						previewWidth={previewWidth}
						side={'front'}
						alt={p.name}
					/>}
				</a>)}
			</div>
			
			<div
				class={`product-slider__arrow product-slider__arrow--left ${leftArrow ? 'product-slider__arrow--visible' : ''}`}
				onClick={e => this.scroll(-1)}
			>
				<Icon name="left-chevron"/>
			</div>
			<div
				class={`product-slider__arrow product-slider__arrow--right ${rightArrow ? 'product-slider__arrow--visible' : ''}`}
				onClick={e => this.scroll(1)}
			>
				<Icon name="right-chevron" />
			</div>
		
		</div>;
	
	}
	
	update = throttle(() => {
		if (!this.content) return;
		const left = this.content.scrollLeft;
		const right = left + this.content.offsetWidth;
		const each = (this.content.children[0] as HTMLElement).offsetWidth;
		this.setState({
			short: this.content.scrollWidth <= this.content.offsetWidth,
			leftArrow: left > 0,
			rightArrow: right < this.content.scrollWidth,
			showUpTo: Math.max(this.state.showUpTo, Math.ceil(right / each) + 2),
		});
	}, 100);
	
	scroll(dir: number) {
		if (!this.content) return;
		const each = (this.content.children[0] as HTMLElement).offsetWidth;
		const curIdx = Math.max(0, Math.floor(this.content.scrollLeft / each));
		const fits = Math.max(1, Math.floor(this.content.offsetWidth / each));
		const newIdx = Math.max(0, curIdx + dir * fits);
		this.content.scrollTo({
			behavior: 'smooth',
			top: 0,
			left: newIdx * each,
		});
	}
	
	componentDidMount() {
		this.update();
		document.body.addEventListener('mousemove', e => {
			if (!this.mouseDown) return;
			e.preventDefault();
			if (Math.abs(e.pageX - this.initialX) > 5) this.dragging = true;
			if (this.dragging) this.content?.scrollTo(this.initialScroll + (this.initialX - e.pageX), 0);
		});
		document.body.addEventListener('touchmove', e => {
			if (!this.mouseDown || !e.touches[0]) return;
			if (Math.abs(e.touches[0].pageX - this.initialX) > 5) this.dragging = true;
			if (this.dragging) this.content?.scrollTo(this.initialScroll + (this.initialX - e.touches[0].pageX), 0);
		});
		const end = (e: Event) => {
			if (this.dragging) e.preventDefault();
			setTimeout(() => {
				this.dragging = this.mouseDown = false;
			}, 100);
		};
		document.body.addEventListener('mouseup', end);
		document.body.addEventListener('touchend', end);
	}
	
}
